/* Snapshot visual de downloads/adoção (ADR-0852). O contrato não tem histórico: barras
   comparam versões no `asOf`; nenhuma série temporal é inferida. */
import type { DownloadsByVersionDto } from '@planuze/admin-contracts';
import { Skeleton, Stat, StatGrid } from '@planuze/ui';
import { MonitorSmartphone, MousePointerClick, Tags, Users } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useDownloadsContent } from '../../content/sections/downloads.js';
import { fmtNumber } from '../../lib/format.js';
import { ChartCard } from './chart-card.js';
import { colorAt } from './chart-palette.js';

type DownloadsItems = DownloadsByVersionDto['items'];

interface DownloadsSnapshotRow {
  readonly version: string;
  readonly stable: number;
  readonly beta: number;
  readonly canary: number;
  readonly activeUsers24h: number;
  readonly activeDevices24h: number;
}

interface DownloadsSnapshot {
  readonly humanClicks: number;
  readonly versions: number;
  readonly activeUsers24h: number;
  readonly activeDevices24h: number;
  readonly rows: readonly DownloadsSnapshotRow[];
}

/** Soma canais defensivamente: o DTO normalmente traz 1 linha/canal, mas o gráfico não depende disso. */
export function buildDownloadsSnapshot(items: DownloadsItems): DownloadsSnapshot {
  let humanClicks = 0;
  let activeUsers24h = 0;
  let activeDevices24h = 0;
  const rows = items.map((item) => {
    const clicks = { stable: 0, beta: 0, canary: 0 };
    for (const download of item.downloads) {
      clicks[download.channel] += download.humanDownloadCount;
      humanClicks += download.humanDownloadCount;
    }
    activeUsers24h += item.activeUsers24h;
    activeDevices24h += item.activeDevices24h;
    return {
      version: item.version,
      ...clicks,
      activeUsers24h: item.activeUsers24h,
      activeDevices24h: item.activeDevices24h,
    };
  });
  return {
    humanClicks,
    versions: items.length,
    activeUsers24h,
    activeDevices24h,
    rows,
  };
}

interface Props {
  readonly data: DownloadsByVersionDto | undefined;
  readonly loading: boolean;
}

export function DownloadsKpis({ data, loading }: Props) {
  const copy = useDownloadsContent();
  if (loading || data === undefined) {
    return (
      <StatGrid columns={4}>
        {['clicks', 'versions', 'users', 'devices'].map((key) => (
          <Skeleton key={key} className="h-24" />
        ))}
      </StatGrid>
    );
  }

  const snapshot = buildDownloadsSnapshot(data.items);
  return (
    <StatGrid columns={4}>
      <Stat
        label={copy.kpis.humanClicks}
        value={fmtNumber(snapshot.humanClicks)}
        hint={copy.kpis.humanClicksHint}
        icon={<MousePointerClick />}
      />
      <Stat
        label={copy.kpis.versions}
        value={fmtNumber(snapshot.versions)}
        hint={copy.kpis.versionsHint}
        icon={<Tags />}
      />
      <Stat
        label={copy.kpis.activeUsers}
        value={fmtNumber(snapshot.activeUsers24h)}
        hint={copy.windowLabel(data.windowHours)}
        icon={<Users />}
      />
      <Stat
        label={copy.kpis.activeDevices}
        value={fmtNumber(snapshot.activeDevices24h)}
        hint={copy.windowLabel(data.windowHours)}
        icon={<MonitorSmartphone />}
      />
    </StatGrid>
  );
}

export function DownloadsCharts({ data, loading }: Props) {
  const copy = useDownloadsContent();
  const chartLoading = loading || data === undefined;
  const snapshot = buildDownloadsSnapshot(data?.items ?? []);
  const rows = snapshot.rows.map((row) => ({
    ...row,
    version: row.version === 'unknown' ? copy.unknownVersion : `v${row.version}`,
  }));
  const channelLabels: Record<'beta' | 'canary' | 'stable', string> = copy.channels;
  const activityLabels: Record<'activeDevices24h' | 'activeUsers24h', string> = {
    activeUsers24h: copy.charts.activeUsers,
    activeDevices24h: copy.charts.activeDevices,
  };

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <ChartCard title={copy.charts.clicksTitle} loading={chartLoading} empty={rows.length === 0}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            accessibilityLayer
            data={[...rows]}
            margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="version" fontSize={12} />
            <YAxis tickFormatter={fmtNumber} fontSize={12} allowDecimals={false} />
            <Tooltip
              formatter={(value: unknown, name: unknown) => [
                fmtNumber(Number(value)),
                channelLabels[String(name) as keyof typeof channelLabels] ?? String(name),
              ]}
            />
            <Legend
              formatter={(value: string) =>
                channelLabels[value as keyof typeof channelLabels] ?? value
              }
            />
            <Bar dataKey="stable" stackId="clicks" fill={colorAt(0)} />
            <Bar dataKey="beta" stackId="clicks" fill={colorAt(1)} />
            <Bar dataKey="canary" stackId="clicks" fill={colorAt(2)} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title={copy.charts.activityTitle} loading={chartLoading} empty={rows.length === 0}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            accessibilityLayer
            data={[...rows]}
            margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="version" fontSize={12} />
            <YAxis tickFormatter={fmtNumber} fontSize={12} allowDecimals={false} />
            <Tooltip
              formatter={(value: unknown, name: unknown) => [
                fmtNumber(Number(value)),
                activityLabels[String(name) as keyof typeof activityLabels] ?? String(name),
              ]}
            />
            <Legend
              formatter={(value: string) =>
                activityLabels[value as keyof typeof activityLabels] ?? value
              }
            />
            <Bar dataKey="activeUsers24h" fill={colorAt(0)} radius={[4, 4, 0, 0]} />
            <Bar dataKey="activeDevices24h" fill={colorAt(4)} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
